import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hasError?: boolean;
  hint?: ReactNode;
}

/**
 * Pill-style checkbox with a custom check glyph — native input is visually
 * hidden but receives focus and preserves form semantics.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { id, label, className, hasError, hint, ...rest },
  ref
) {
  return (
    <label
      htmlFor={id}
      className={cn('flex items-start gap-3 cursor-pointer group', className)}
    >
      <span className="relative mt-0.5 shrink-0">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className="peer sr-only"
          {...rest}
        />
        <span
          aria-hidden="true"
          className={cn(
            'block h-5 w-5 rounded-md border-2 transition-colors',
            'group-hover:border-brand-purple',
            'peer-checked:bg-brand-yellow peer-checked:border-brand-yellow',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-yellow',
            hasError ? 'border-red-500' : 'border-brand-lightgray'
          )}
        />
        <Check
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 m-auto h-4 w-4 text-brand-purple opacity-0 peer-checked:opacity-100 transition-opacity"
        />
      </span>
      <span className="text-sm text-brand-purple/90 leading-snug">
        {label}
        {hint && <span className="block text-xs text-brand-purple/60 mt-1">{hint}</span>}
      </span>
    </label>
  );
});
